"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { FaArrowRight, FaCheck } from "react-icons/fa6";
import GradientButton from "@/components/ui/GradientButton";

const stats = [
  { value: "50", label: "Prompts tested", color: "text-indigo-950 dark:text-slate-100" },
  { value: "18", label: "Brand mentions", color: "text-emerald-700 dark:text-emerald-300" },
  { value: "9", label: "Citations", color: "text-violet-700 dark:text-violet-300" },
  { value: "14", label: "Competitor gaps", color: "text-rose-700 dark:text-rose-300" },
];

const opportunities = [
  { text: "AI crawlers blocked in robots.txt", level: "Critical", color: "bg-rose-500" },
  { text: "Add Organization & FAQ schema", level: "High", color: "bg-orange-500" },
  { text: "Create comparison pages", level: "High", color: "bg-orange-500" },
  { text: "Strengthen author bios", level: "Medium", color: "bg-amber-400" },
  { text: "Get listed on review sites", level: "Medium", color: "bg-amber-400" },
];

const engineShare = [
  { name: "ChatGPT", value: 36, color: "bg-emerald-500" },
  { name: "Google AI Overviews", value: 28, color: "bg-blue-500" },
  { name: "Perplexity", value: 44, color: "bg-violet-500" },
  { name: "Gemini", value: 22, color: "bg-pink-500" },
];

const reportPoints = [
  "Visibility score across major AI engines",
  "Prompts where competitors appear instead of you",
  "Prioritised technical and content fixes",
  "Expert review and a 90-day action plan",
];

export default function AiSearchSampleReport() {
  const reduceMotion = useReducedMotion();
  const reportRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(reportRef, { once: true, amount: 0.2 });
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const progress = isInView && reduceMotion ? 1 : animatedProgress;

  useEffect(() => {
    if (!isInView) return;

    if (reduceMotion) return;

    const startTime = performance.now();
    const duration = 1500;
    let frameId = 0;

    const updateProgress = (now: number) => {
      const elapsed = Math.min((now - startTime) / duration, 1);
      setAnimatedProgress(1 - Math.pow(1 - elapsed, 3));

      if (elapsed < 1) {
        frameId = window.requestAnimationFrame(updateProgress);
      }
    };

    frameId = window.requestAnimationFrame(updateProgress);
    return () => window.cancelAnimationFrame(frameId);
  }, [isInView, reduceMotion]);

  return (
    <motion.section
      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: reduceMotion ? 0 : 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="overflow-hidden bg-linear-to-br from-amber-50 to-pink-50 px-4 py-12 sm:px-6 sm:py-16 lg:px-8 dark:from-gray-950 dark:to-slate-900"
    >
      <div className="mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-2 lg:gap-14">
        <div
          ref={reportRef}
          role="img"
          aria-label="Sample AI visibility report: score 64, 50 prompts tested, 18 brand mentions, 9 citations, and 14 competitor gaps."
          className="relative flex flex-col gap-4 md:pb-36"
        >
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            transition={{ duration: reduceMotion ? 0 : 0.55, ease: "easeOut" }}
            className="rounded-2xl bg-white p-5 shadow-xl shadow-indigo-950/10 sm:p-6 dark:bg-slate-900"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-poppins text-base font-bold text-indigo-950 dark:text-white">
                AI Visibility Overview
              </h3>
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-800 dark:bg-amber-950/60 dark:text-amber-200">
                Sample report
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-5">
              <div className="flex flex-col items-center">
                <div className="relative h-16 w-32">
                  <svg
                    viewBox="0 0 120 70"
                    className="absolute inset-0 h-16 w-32"
                    aria-hidden="true"
                  >
                    <defs>
                      <linearGradient id="ai-report-score" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0" stopColor="#7b3fe4" />
                        <stop offset="0.6" stopColor="#e11d63" />
                        <stop offset="1" stopColor="#f7845a" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M10 60a50 50 0 0 1 100 0"
                      fill="none"
                      stroke="#f1f2f7"
                      strokeWidth="10"
                      strokeLinecap="round"
                      className="dark:stroke-slate-700"
                    />
                    <motion.path
                      d="M10 60a50 50 0 0 1 100 0"
                      fill="none"
                      stroke="url(#ai-report-score)"
                      strokeWidth="10"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={isInView ? { pathLength: 0.64 } : { pathLength: 0 }}
                      transition={{ duration: reduceMotion ? 0 : 1.5, ease: "easeOut" }}
                    />
                  </svg>
                  <span className="absolute inset-x-0 bottom-0 text-center font-poppins text-3xl font-extrabold leading-none text-indigo-950 dark:text-white">
                    {Math.round(progress * 64)}
                  </span>
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  Visibility score
                </span>
              </div>

              <div className="grid min-w-0 flex-1 grid-cols-2 gap-3 text-center sm:grid-cols-4">
                {stats.map((stat) => (
                  <div key={stat.label}>
                    <p className={`font-poppins text-xl font-extrabold ${stat.color}`}>
                      {Math.round(progress * Number(stat.value))}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            animate={
              isInView
                ? { opacity: 1, y: reduceMotion ? 0 : [0, 8, 0] }
                : { opacity: 0, y: 12 }
            }
            transition={{
              opacity: {
                duration: reduceMotion ? 0 : 0.55,
                delay: 0.15,
              },
              y: {
                duration: reduceMotion ? 0 : 6,
                delay: 0.15,
                repeat: reduceMotion ? 0 : Infinity,
                ease: "easeInOut",
              },
            }}
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-indigo-950/10 md:absolute md:top-40 md:left-0 md:w-7/12 dark:border-slate-700 dark:bg-slate-900"
          >
            <h3 className="font-poppins text-sm font-bold text-indigo-950 dark:text-white">
              Top Opportunities
            </h3>
            <ul className="mt-3 flex flex-col gap-2">
              {opportunities.map((item) => (
                <li
                  key={item.text}
                  className="flex min-w-0 items-center gap-2 text-xs text-slate-700 dark:text-slate-300"
                >
                  <span className={`size-2 shrink-0 rounded-full ${item.color}`} />
                  <span className="min-w-0 flex-1">{item.text}</span>
                  <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold dark:bg-slate-800">
                    {item.level}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            animate={
              isInView
                ? { opacity: 1, y: reduceMotion ? 0 : [0, 8, 0] }
                : { opacity: 0, y: 12 }
            }
            transition={{
              opacity: {
                duration: reduceMotion ? 0 : 0.55,
                delay: 0.3,
              },
              y: {
                duration: reduceMotion ? 0 : 6.5,
                delay: 0.3,
                repeat: reduceMotion ? 0 : Infinity,
                ease: "easeInOut",
              },
            }}
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-indigo-950/10 md:absolute md:top-35 md:right-0 md:w-5/12 dark:border-slate-700 dark:bg-slate-900"
          >
            <h3 className="font-poppins text-sm font-bold text-indigo-950 dark:text-white">
              Mentions by Engine
            </h3>
            <ul className="mt-3 flex flex-col gap-3">
              {engineShare.map((engine) => (
                <li key={engine.name}>
                  <div className="flex justify-between gap-2 text-xs text-slate-600 dark:text-slate-300">
                    <span className="truncate">{engine.name}</span>
                    <span className="font-semibold">{engine.value}%</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: isInView ? `${engine.value}%` : 0 }}
                      transition={{
                        duration: reduceMotion ? 0 : 1.1,
                        delay: reduceMotion ? 0 : 0.2,
                        ease: "easeOut",
                      }}
                      className={`h-full rounded-full ${engine.color}`}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <div className="flex flex-col items-start gap-5">
          <p className="font-poppins text-sm font-bold uppercase tracking-widest text-pink-700 dark:text-pink-400">
            Sample Report
          </p>
          <h2 className="font-poppins text-3xl font-bold leading-tight text-indigo-950 sm:text-4xl dark:text-white">
            See Exactly How <span className="text-violet-700 dark:text-violet-400">AI</span>
            <br className="hidden sm:block" /> Sees Your Brand
          </h2>
          <p className="max-w-2xl text-base leading-relaxed text-slate-600 dark:text-slate-300">
            We test real customer questions across leading AI engines and show
            you where you&apos;re mentioned, where competitors win, and what to
            fix first.
          </p>
          <ul className="flex flex-col gap-3 text-sm font-medium text-indigo-950 sm:text-base dark:text-slate-100">
            {reportPoints.map((point) => (
              <li key={point} className="flex items-center gap-3">
                <span className="grid size-6 shrink-0 place-items-center rounded-full bg-pink-600 text-white">
                  <FaCheck aria-hidden className="text-xs" />
                </span>
                {point}
              </li>
            ))}
          </ul>
          {/* <GradientButton
            href="#ai-search-url"
            variant="sunset-pink"
            className="h-auto min-h-14 px-6 font-semibold"
          >
            Get Your Free AI Report <FaArrowRight aria-hidden />
          </GradientButton> */}
        </div>
      </div>
    </motion.section>
  );
}
